import { ArrowLeft, CheckCircle2, RefreshCw, Timer } from 'lucide-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../contexts/AuthContext';
import { VerificationCodeInput, VerificationCodeInputRef } from '../components/forms';
import { authService } from '../services/authService';

interface VerificationFormData {
  code: string;
}

const VerificationCode: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const verificationRef = useRef<VerificationCodeInputRef>(null);

  const { handleSubmit, setValue, watch } = useForm<VerificationFormData>();

  const code = watch('code') || '';

  useEffect(() => {
    // Get email from localStorage (set during registration)
    const registrationEmail = localStorage.getItem('registration_email');
    if (registrationEmail) {
      setEmail(registrationEmail);
      // Start countdown timer for resend (60 seconds)
      setResendCountdown(60);
      setCanResend(false);
    } else {
      // If no email found, redirect to registration
      navigate('/register');
    }
  }, [navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const onSubmit = async (data: VerificationFormData) => {
    if (data.code.length !== 4) {
      setError(t('auth.pleaseEnterCompleteCode'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await authContext?.verifyCode({ email, code: data.code });
      if (result?.success) {
        // Show success message
        setShowSuccess(true);

        // Clear stored email after successful verification
        localStorage.removeItem('registration_email');

        // Navigate to dashboard after showing success
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(result?.message || t('auth.invalidVerificationCode'));
        setShowSuccess(false);
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setIsResending(true);
    setError(null);
    setShowSuccess(false);

    try {
      const response = await authService.resendVerificationCode(email);
      if (response.success) {
        verificationRef.current?.clear();
        setError(null);

        // Start countdown timer again
        setResendCountdown(60);
        setCanResend(false);

        // Show success message for resend
        setError(null);
      } else {
        setError(response.message || t('auth.failedToResendCode'));
      }
    } catch {
      setError(t('auth.failedToResendCode'));
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToRegister = () => {
    navigate('/register');
  };

  const handleCodeChange = (newCode: string) => {
    setValue('code', newCode);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Orange background with verification illustration */}
      <div className="flex-1 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-brand-300 rounded-full opacity-30"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-brand-300 rounded-full opacity-20"></div>
          <div className="absolute top-1/2 left-8 w-16 h-16 bg-brand-300 rounded-full opacity-25"></div>
        </div>

        <div className="relative z-10 max-w-lg">
          {/* Text content */}
          <div className="bg-brand-400 bg-opacity-80 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
              Connected Construction : Top Technology Trends
            </h1>
          </div>

          {/* Verification illustration */}
          <div className="flex justify-center">
            <div className="relative">
              <svg viewBox="0 0 400 300" className="w-80 h-60">
                <g>
                  {/* Desk */}
                  <rect x="50" y="200" width="300" height="20" fill="#D4A574" rx="4" />
                  <rect x="50" y="220" width="300" height="50" fill="#B8956A" rx="4" />

                  {/* Laptop */}
                  <rect x="150" y="170" width="80" height="45" fill="#2D3748" rx="4" />
                  <rect x="155" y="175" width="70" height="35" fill="#4A5568" rx="2" />
                  <rect x="160" y="180" width="60" height="25" fill="#E2E8F0" rx="1" />

                  {/* Person sitting */}
                  <g transform="translate(250, 120)">
                    {/* Head */}
                    <circle cx="30" cy="30" r="15" fill="#F4A261" />

                    {/* Hair */}
                    <path d="M15 25 Q30 15 45 25 Q45 20 30 18 Q15 20 15 25" fill="#2D3748" />

                    {/* Face - happy expression */}
                    <circle cx="26" cy="28" r="1.5" fill="#2D3748" />
                    <circle cx="34" cy="28" r="1.5" fill="#2D3748" />
                    <path d="M24 33 Q30 36 36 33" stroke="#2D3748" strokeWidth="1" fill="none" />

                    {/* Body */}
                    <rect x="20" y="45" width="20" height="35" fill="#E76F51" rx="8" />

                    {/* Hand with phone */}
                    <circle cx="10" cy="50" r="4" fill="#F4A261" />
                    <rect x="5" y="45" width="10" height="15" fill="#2D3748" rx="2" />
                    <rect x="6" y="46" width="8" height="10" fill="#4A5568" rx="1" />

                    {/* Chair back */}
                    <rect x="45" y="35" width="8" height="50" fill="#2D3748" rx="4" />

                    {/* Coffee cup */}
                    <rect x="60" y="70" width="12" height="15" fill="#8B4513" rx="2" />
                    <rect x="62" y="72" width="8" height="10" fill="#D2691E" rx="1" />
                    <path
                      d="M72 75 Q76 75 76 78 Q76 81 72 81"
                      stroke="#8B4513"
                      strokeWidth="1"
                      fill="none"
                    />

                    {/* Steam lines */}
                    <path d="M66 68 Q68 64 66 60" stroke="#9CA3AF" strokeWidth="1" fill="none" />
                    <path d="M70 68 Q72 64 70 60" stroke="#9CA3AF" strokeWidth="1" fill="none" />

                    {/* Plant */}
                    <rect x="80" y="75" width="6" height="10" fill="#8B4513" />
                    <circle cx="81" cy="73" r="3" fill="#10B981" />
                    <circle cx="85" cy="71" r="3" fill="#10B981" />
                    <circle cx="83" cy="68" r="3" fill="#10B981" />
                  </g>

                  {/* Verification notification popup */}
                  <g transform="translate(100, 60)">
                    <rect
                      x="0"
                      y="0"
                      width="120"
                      height="80"
                      fill="white"
                      rx="8"
                      stroke="#E5E7EB"
                      strokeWidth="2"
                    />

                    {/* User avatar in popup */}
                    <circle cx="25" cy="25" r="12" fill="#E59449" />
                    <circle cx="25" cy="20" r="6" fill="#F4A261" />
                    <path d="M15 30 Q25 27 35 30" fill="#F4A261" />

                    {/* Notification text */}
                    <text x="45" y="20" fontSize="8" fill="#4B5563">
                      ENTER CODE
                    </text>
                    <rect x="45" y="25" width="60" height="8" fill="#F3F4F6" rx="2" />
                    <text x="50" y="31" fontSize="6" fill="#9CA3AF">
                      Verification
                    </text>

                    {/* Done button */}
                    <rect x="45" y="40" width="60" height="15" fill="#E59449" rx="3" />
                    <text x="70" y="50" fontSize="8" fill="white" textAnchor="middle">
                      DONE
                    </text>
                  </g>

                  {/* Arrow pointing from phone to popup */}
                  <path
                    d="M210 150 Q180 120 150 100 L155 105 M150 100 L160 95"
                    stroke="#1A202C"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="3,2"
                  />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Verification form */}
      <div className="flex-1 bg-white flex items-center justify-center p-12 relative">
        {/* Back button */}
        <button
          onClick={handleBackToRegister}
          className="absolute top-8 right-8 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>

        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verification Code</h2>
            <p className="text-gray-600">
              We sent a verification code to{' '}
              <span className="font-medium text-gray-900">{email}</span>
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {showSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 className="text-green-500 mr-2" size={20} />
                <div>
                  <p className="text-sm text-green-800 font-medium">Email Verified Successfully!</p>
                  <p className="text-xs text-green-600 mt-1">
                    Welcome to builderbel! Redirecting you to dashboard...
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Verification Code Inputs */}
            <VerificationCodeInput
              ref={verificationRef}
              label="Verification Code"
              onChange={handleCodeChange}
              error={
                error && code.length < 4 ? 'Please enter the complete verification code' : undefined
              }
            />

            {/* Resend link */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending || !canResend}
                  className="text-brand-primary hover:text-brand-primary-dark font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? (
                    <span className="flex items-center">
                      <RefreshCw className="animate-spin mr-1" size={12} />
                      Sending...
                    </span>
                  ) : !canResend ? (
                    <span className="flex items-center">
                      <Timer className="mr-1" size={12} />
                      Resend in {resendCountdown}s
                    </span>
                  ) : (
                    'Resend Code'
                  )}
                </button>
              </p>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || code.length !== 4}
              className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>Verifying...</span>
                </div>
              ) : (
                'SUBMIT'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerificationCode;
